import { Link } from "react-router-dom";
import { Sparkles, Scissors, Flower2, HandHeart, ArrowUpRight, Star } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1773904215697-e6c21fc27ac2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBzYWxvbiUyMGludGVyaW9yfGVufDB8fHx8MTc4MzA3NzI2MHww&ixlib=rb-4.1.0&q=85";
const INTERIOR = "https://images.pexels.com/photos/13068377/pexels-photo-13068377.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const SPA = "https://images.unsplash.com/photo-1741522509438-a120c0bb5e88?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwyfHxzcGElMjB3ZWxsbmVzcyUyMG1hc3NhZ2V8ZW58MHx8fHwxNzgzMDc3MjYwfDA&ixlib=rb-4.1.0&q=85";
const WELLNESS = "https://images.pexels.com/photos/9146381/pexels-photo-9146381.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Landing() {
  return (
    <div className="min-h-screen bg-emerald-base text-emerald-text overflow-x-hidden" data-testid="landing-page">
      {/* Nav */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-panel">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 md:px-10 py-4">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
            <Sparkles className="w-5 h-5 text-gold" />
            <span className="font-heading text-2xl tracking-wide">SOS (Stan Of Style)</span>
            <span className="overline hidden sm:inline">Luxury Salon & Spa</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-emerald-muted">
            <a href="#services" className="hover:text-gold transition">Services</a>
            <a href="#experience" className="hover:text-gold transition">Experience</a>
            <a href="#about" className="hover:text-gold transition">About</a>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="nav-login-link" className="text-sm text-emerald-muted hover:text-gold transition">Sign in</Link>
            <Link to="/register" data-testid="nav-book-btn" className="gold-button px-5 py-2 rounded-full text-sm inline-flex items-center gap-2">
              Book Now <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative min-h-screen flex items-end pt-32 pb-20 px-6 md:px-12 lg:px-16">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Luxury salon" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#051A11]/70 via-[#051A11]/40 to-[#051A11]"></div>
        </div>
        <div className="relative max-w-7xl mx-auto w-full grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-8 fade-up">
            <div className="overline mb-6 delay-100 fade-up">A House of Rituals — Est. 2019</div>
            <h1 className="font-heading text-5xl sm:text-7xl lg:text-8xl leading-[0.95] tracking-tight text-white delay-200 fade-up">
              Where beauty is <em className="text-gold not-italic">quietly</em>
              <br />
              made <em className="not-italic">extraordinary</em>.
            </h1>
            <p className="mt-8 max-w-xl text-lg text-emerald-muted delay-300 fade-up">
              A bespoke luxury salon and spa concept — combining editorial styling, restorative rituals,
              and a dedicated management platform for our team and guests.
            </p>
            <div className="mt-10 flex flex-wrap gap-4 delay-400 fade-up">
              <Link to="/register" data-testid="hero-book-btn" className="gold-button px-8 py-3.5 rounded-full inline-flex items-center gap-2">
                Reserve an Experience <ArrowUpRight className="w-4 h-4" />
              </Link>
              <Link to="/login" data-testid="hero-signin-link" className="gold-outline-button px-8 py-3.5 rounded-full">
                Team Sign in
              </Link>
            </div>
          </div>
          <div className="lg:col-span-4 hidden lg:block">
            <div className="glass-panel rounded-2xl p-6 fade-up delay-400">
              <div className="overline mb-3">Signature Ritual</div>
              <h3 className="font-heading text-3xl text-white leading-tight">The SOS Golden Hour</h3>
              <p className="text-sm text-emerald-muted mt-3">Facial · Scalp · Hands — 90 minutes of restoration.</p>
              <div className="mt-6 pt-6 border-t border-gold-subtle flex items-center justify-between">
                <span className="text-gold font-medium text-xl">Rs.3000.00</span>
                <span className="text-xs text-emerald-muted">Available daily</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services strip */}
      <section id="services" className="max-w-7xl mx-auto px-6 md:px-12 py-24">
        <div className="grid lg:grid-cols-12 gap-8 items-end mb-14">
          <div className="lg:col-span-8">
            <div className="overline mb-4">Our Craft</div>
            <h2 className="font-heading text-4xl sm:text-5xl text-white tracking-tight">Four disciplines, one philosophy.</h2>
          </div>
          <p className="lg:col-span-4 text-emerald-muted">Every treatment at SOS is designed as a quiet ritual — technical mastery layered with sensory calm.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: Scissors, title: "Hair", note: "Editorial cuts, colour & finishing." },
            { icon: Flower2, title: "Facial", note: "Bespoke skincare protocols." },
            { icon: HandHeart, title: "Spa", note: "Massage & restorative body work." },
            { icon: Sparkles, title: "Nails", note: "Sculpted manicures & artistry." },
          ].map(({ icon: Icon, title, note }, i) => (
            <div key={title} data-testid={`service-tile-${title.toLowerCase()}`} className="bg-emerald-surface border border-gold-subtle rounded-2xl p-8 hover:border-gold transition group">
              <Icon className="w-6 h-6 text-gold mb-8 group-hover:scale-110 transition" />
              <div className="font-heading text-3xl text-white">{title}</div>
              <p className="text-sm text-emerald-muted mt-3">{note}</p>
              <div className="mt-8 text-xs text-gold tracking-widest">0{i + 1}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Experience */}
      <section id="experience" className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7 relative rounded-3xl overflow-hidden aspect-[4/3]">
            <img src="/SOS-15-Percent-image.jpeg" alt="15% off at SOS" className="w-full h-full object-cover" />
          </div>
          <div className="lg:col-span-5 flex flex-col justify-center">
            <div className="overline mb-4">A Room of One's Own</div>
            <h2 className="font-heading text-4xl sm:text-5xl text-white leading-tight">Interiors imagined as sanctuary.</h2>
            <p className="mt-6 text-emerald-muted">Warm timber, aged brass, and a hush that lets the ritual speak. Each seat is a small stage — and each guest, the story.</p>
            <div className="mt-10 grid grid-cols-3 gap-6 border-t border-gold-subtle pt-8">
              <div><div className="font-heading text-3xl text-gold">12+</div><div className="text-xs text-emerald-muted mt-1">Years of craft</div></div>
              <div><div className="font-heading text-3xl text-gold">30</div><div className="text-xs text-emerald-muted mt-1">Signature rituals</div></div>
              <div><div className="font-heading text-3xl text-gold">4.9</div><div className="text-xs text-emerald-muted mt-1 flex items-center gap-1">Guest rating <Star className="w-3 h-3 fill-gold text-gold" /></div></div>
            </div>
          </div>
        </div>
      </section>

      {/* Duo images */}
      <section id="about" className="max-w-7xl mx-auto px-6 md:px-12 py-24">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="rounded-3xl overflow-hidden aspect-[4/5]">
            <img src={SPA} alt="Massage" className="w-full h-full object-cover" />
          </div>
          <div className="rounded-3xl overflow-hidden aspect-[4/5] mt-12 md:mt-24">
            <img src={WELLNESS} alt="Wellness" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-20 text-center">
        <div className="overline mb-6">Reserve your seat</div>
        <h2 className="font-heading text-5xl sm:text-6xl text-white leading-tight">Begin your ritual.</h2>
        <p className="mt-6 text-emerald-muted max-w-xl mx-auto">Create your guest profile, choose a stylist, and book your first experience.</p>
        <div className="mt-10 flex justify-center gap-4">
          <Link to="/register" data-testid="cta-register-btn" className="gold-button px-10 py-4 rounded-full inline-flex items-center gap-2">
            Create Guest Profile <ArrowUpRight className="w-4 h-4" />
          </Link>
          <Link to="/login" data-testid="cta-login-link" className="gold-outline-button px-10 py-4 rounded-full">Sign in</Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gold-subtle mt-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 font-heading text-xl">
            <Sparkles className="w-4 h-4 text-gold" /> SOS Luxury Salon & Spa
          </div>
          <p className="text-xs text-emerald-muted">© {new Date().getFullYear()} SOS. All rituals reserved.</p>
        </div>
      </footer>
    </div>
  );
}
